import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ShieldCheck, Clock, Send, Loader2, AlertTriangle, CheckCircle2, Info, Eye,
} from 'lucide-react';
import { getProofSubmission, submitProofResponse } from '../services/api';
import { useIntegritySignals } from '../hooks/useIntegritySignals';

// Feature 2 pillar 2 — OPT-IN active face liveness (camera). Default OFF.
// Flag-gated AND lazy so neither the component nor MediaPipe ever land in the
// initial bundle when the flag is off. When off, ProofComplete is unchanged.
const LIVENESS_ENABLED = process.env.REACT_APP_ENABLE_LIVENESS === 'true';
const LivenessCheck = LIVENESS_ENABLED
  ? React.lazy(() => import('../components/LivenessCheck'))
  : null;

/* ----------------------------------------------------------------------------
 * Screen B — the candidate's timed work-sample completion screen.
 * Distraction-free (no recruiter chrome), neutral + signal-green. Integrity
 * signals (tab focus, paste, automation) are captured into the hash-chained
 * audit log from the first render — log-only, disclosed up front, no biometrics.
 * -------------------------------------------------------------------------- */

const C = {
  ink: '#0F172A', slate700: '#334155', slate500: '#64748B', slate400: '#94A3B8',
  hairline: '#E2E8F0', page: '#F8FAFC', surface: '#FFFFFF',
  green: '#2563EB', greenInk: '#1D4FD8', greenSoft: '#EFF4FE', greenLine: '#BFDBFE',
  amber: '#B45309', amberSoft: '#FFF8EB', amberLine: '#F4E2BE', track: '#EEF2F6',
};
const serif = "'Source Serif 4', Georgia, 'Times New Roman', serif";

const fmtClock = (ms) => {
  if (ms == null) return '—';
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

function Card({ children, style }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 12, ...style }}>
      {children}
    </div>
  );
}

function ProofComplete() {
  const { id } = useParams();
  const answerRef = useRef(null);

  const [sub, setSub] = useState(null);
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [showNotice, setShowNotice] = useState(false);
  // advisory only — liveness NEVER blocks submission (null = not yet attempted)
  const [livenessStatus, setLivenessStatus] = useState(null);

  const { finalize } = useIntegritySignals(id, { answerRef });

  useEffect(() => {
    let active = true;
    setLoading(true);
    getProofSubmission(id)
      .then((res) => {
        if (!active) return;
        setSub(res.data.submission || null);
        setWs(res.data.work_sample || null);
        if ((res.data.submission || {}).status === 'submitted' || (res.data.submission || {}).status === 'scored') setDone(true);
        setLoading(false);
      })
      .catch((e) => {
        if (!active) return;
        setError(e.response?.data?.error || 'Could not load this assessment.');
        setLoading(false);
      });
    return () => { active = false; };
  }, [id]);

  // countdown against the server deadline
  useEffect(() => {
    if (!sub?.deadline_at) { setRemaining(null); return; }
    const tick = () => setRemaining(new Date(sub.deadline_at).getTime() - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [sub]);

  const expired = remaining != null && remaining <= 0;
  const lowTime = remaining != null && remaining > 0 && remaining < 5 * 60 * 1000;

  const handleSubmit = useCallback(async () => {
    if (!answer.trim()) { toast.error('Please write your answer before submitting.'); return; }
    setSubmitting(true);
    try {
      await finalize(answer);                       // flush integrity chain incl. typed-vs-pasted summary
      await submitProofResponse(id, { response_text: answer });
      setDone(true);
      toast.success('Submitted — thank you.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [answer, finalize, id]);

  return (
    <div style={{ minHeight: '100vh', background: C.page, color: C.ink }}>
      {/* top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'saturate(180%) blur(8px)', borderBottom: `1px solid ${C.hairline}`,
      }}>
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: serif, fontWeight: 600, fontSize: 17 }}>
            <span style={{ width: 24, height: 24, borderRadius: 6, background: C.ink, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={14} color="#fff" strokeWidth={2.4} />
            </span>
            AutoHire
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span title="Session integrity is being recorded for review" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 500, color: C.greenInk }}>
              <ShieldCheck size={14} /> Monitored
            </span>
            {sub?.deadline_at && !done && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 600,
                fontVariantNumeric: 'tabular-nums', padding: '5px 12px', borderRadius: 8,
                background: expired ? C.amberSoft : lowTime ? C.amberSoft : C.greenSoft,
                color: expired || lowTime ? C.amber : C.greenInk,
                border: `1px solid ${expired || lowTime ? C.amberLine : C.greenLine}`,
              }}>
                <Clock size={14} /> {expired ? 'Time up' : fmtClock(remaining)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 56px' }}>
        {loading && (
          <Card style={{ padding: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: C.slate500 }}>
            <Loader2 size={18} className="animate-spin" /> Loading assessment…
          </Card>
        )}

        {!loading && error && (
          <Card style={{ padding: 32, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertTriangle size={20} style={{ color: C.amber, flex: 'none', marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{error}</div>
              <div style={{ fontSize: 13.5, color: C.slate500 }}>This link may have expired, or you may need to sign in as the candidate it was sent to.</div>
            </div>
          </Card>
        )}

        {!loading && !error && done && (
          <Card style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.greenSoft, border: `1px solid ${C.greenLine}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <CheckCircle2 size={28} style={{ color: C.green }} />
            </div>
            <h1 style={{ fontFamily: serif, fontSize: 24, fontWeight: 600, margin: '0 0 8px' }}>Response submitted</h1>
            <p style={{ fontSize: 14.5, color: C.slate500, maxWidth: 440, margin: '0 auto', lineHeight: 1.6 }}>
              Thanks — your work sample has been received and will be scored against the role rubric. You can close this window.
            </p>
          </Card>
        )}

        {!loading && !error && !done && (
          <>
            {/* disclosure banner */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '12px 16px', background: C.greenSoft, border: `1px solid ${C.greenLine}`, borderRadius: 10, marginBottom: 20 }}>
              <Eye size={17} style={{ color: C.greenInk, flex: 'none', marginTop: 1 }} />
              <div style={{ fontSize: 13, color: C.slate700, lineHeight: 1.55 }}>
                This is a monitored assessment. We record <strong>integrity signals only</strong> — when the tab loses focus, when text is pasted, and an automated-browser check — to keep results fair. <strong>No camera, microphone, or biometric data.</strong>{' '}
                <button onClick={() => setShowNotice((s) => !s)} style={{ color: C.greenInk, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {showNotice ? 'Hide details' : 'What is recorded?'}
                </button>
                {showNotice && (
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: C.slate500 }}>
                    <li>Tab/window focus changes during the assessment</li>
                    <li>Paste events and how much of the answer was typed vs pasted</li>
                    <li>A one-time automated-browser (bot) check</li>
                    <li>Signals are advisory and reviewed by a person — they never auto-reject you</li>
                  </ul>
                )}
              </div>
            </div>

            {/* prompt */}
            <Card style={{ overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.hairline}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.green, marginBottom: 8 }}>
                  {ws?.role_family ? `${ws.role_family} · work sample` : 'Work sample'}
                </div>
                <h1 style={{ fontFamily: serif, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', margin: 0 }}>
                  {ws?.title || 'Assessment'}
                </h1>
              </div>
              <div style={{ padding: '20px 22px', fontSize: 14.5, color: C.slate700, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {ws?.prompt_md || 'No prompt provided.'}
              </div>
            </Card>

            {/* OPTIONAL camera liveness (flag-gated, lazy, advisory). Renders only
                when REACT_APP_ENABLE_LIVENESS==='true'; never blocks submission. */}
            {LIVENESS_ENABLED && LivenessCheck && (
              <Suspense fallback={null}>
                <LivenessCheck
                  submissionId={id}
                  onComplete={(r) => setLivenessStatus(r?.status || null)}
                  onSkip={() => setLivenessStatus('skipped')}
                />
              </Suspense>
            )}

            {/* answer */}
            <Card style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: `1px solid ${C.hairline}` }}>
                <h3 style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, margin: 0 }}>Your answer</h3>
                <span style={{ fontSize: 12.5, color: C.slate400 }}>{answer.length.toLocaleString()} characters</span>
              </div>
              <textarea
                ref={answerRef}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={expired || submitting}
                placeholder="Write your response here. Explain your approach, include any code or pseudocode, and discuss tradeoffs."
                style={{
                  width: '100%', minHeight: 320, border: 'none', outline: 'none', resize: 'vertical',
                  padding: '18px 22px', fontSize: 14.5, lineHeight: 1.7, color: C.ink,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: expired ? C.page : C.surface,
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 22px', borderTop: `1px solid ${C.hairline}`, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: C.slate500 }}>
                  <Info size={14} style={{ color: C.slate400 }} />
                  {expired
                    ? 'The deadline has passed — submissions are closed.'
                    : LIVENESS_ENABLED && livenessStatus && livenessStatus !== 'passed'
                      ? 'The optional camera step is advisory — you can submit either way.'
                      : 'Your answer is timed and saved on submit.'}
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={expired || submitting || !answer.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600,
                    color: '#fff', background: expired || !answer.trim() ? C.slate400 : C.green,
                    border: `1px solid ${expired || !answer.trim() ? C.slate400 : C.green}`,
                    borderRadius: 8, padding: '10px 20px',
                    cursor: expired || submitting || !answer.trim() ? 'default' : 'pointer',
                  }}
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} strokeWidth={2} />}
                  Submit answer
                </button>
              </div>
            </Card>
          </>
        )}
      </div>

      <style>{`
        .animate-spin { animation: pcspin 1s linear infinite; }
        @keyframes pcspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default ProofComplete;
